import React, { useContext, useEffect, useMemo, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import {
  APPOINTMENT_ID,
  APP_URL,
  DEFAULT_SCHEDULE_APPOINTMENT_URL,
  fspLogo,
  isObjectEmpty,
  currentTeamId,
  getValidationMessage,
  checkErrorExists,
} from "../../Utils/utils";
import SignupSlider from "./SignupSlider";
import "../Login/login.scss";
import Session from "../../Utils/Session";
import { useTranslation } from "react-i18next";
import InLineError from "../../Components/Common/AlertMessage/InLineError";
import { AuthContext } from "../../Context/AuthContext";

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const getNamePart = (fullName, index) => {
  if (!fullName) {
    return "";
  }

  const parts = String(fullName).trim().split(/\s+/);
  if (index === 0) {
    return parts[0] || "";
  }

  return parts.slice(1).join(" ");
};

const appendParamIfPresent = (params, key, value) => {
  if (value !== undefined && value !== null && String(value).trim() !== "") {
    params.set(key, String(value).trim());
  }
};

const EmailVerification = ({
  email,
  changeEmailForUnconfirmed,
  fetchUserSettings,
  isUserActive,
  emailStatus = null,
}) => {
  const { t: locale } = useTranslation(["common", "pages"]);
  const { userData } = useContext(AuthContext);
  const authData = userData?.authData || {};
  const [isChangeEmail, setIsChangeEmail] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [resendCount, setResendCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changedEmail, setChangedEmail] = useState(email || authData?.email || "");
  const resolvedEmail = changedEmail || email || authData?.email || "";
  const firstName = authData?.first_name || getNamePart(authData?.name, 0);
  const lastName = authData?.last_name || getNamePart(authData?.name, 1);
  const mobile = authData?.phone_no || authData?.phone_number || authData?.mobile || "";
  const scheduleAppointmentUrl = useMemo(() => {
    const baseUrl =
      DEFAULT_SCHEDULE_APPOINTMENT_URL ||
      (APPOINTMENT_ID ? `${APP_URL}/schedule-appointment/${APPOINTMENT_ID}` : "");

    if (!baseUrl) {
      return "#";
    }

    try {
      const url = new URL(baseUrl);
      appendParamIfPresent(url.searchParams, "first_name", firstName);
      appendParamIfPresent(url.searchParams, "last_name", lastName);
      appendParamIfPresent(url.searchParams, "email", resolvedEmail);
      appendParamIfPresent(url.searchParams, "mobile", mobile);

      return url.toString();
    } catch (error) {
      return baseUrl;
    }
  }, [firstName, lastName, mobile, resolvedEmail]);

  useEffect(() => {
    let count = 0;
    try {
      count = Session.getSessionVar("resend");
      count = isObjectEmpty(count) ? 0 : count;
    } catch (e) {
      console.log(e);
      count = 1;
    }

    setResendCount(parseInt(count));
    // setStatus(
    //   <span className="text-success font-weight-bold">
    //     {locale("Verification email sent successfully to {{newEmail}}", {
    //       newEmail: email,
    //     })}
    //   </span>
    // );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (["valid", "", null].indexOf(emailStatus) === -1) {
      setIsChangeEmail(true);
    }
  }, [emailStatus]);

  useEffect(() => {
    if (!changedEmail && (email || authData?.email)) {
      setChangedEmail(email || authData.email);
    }
  }, [authData?.email, changedEmail, email]);

  const validateEmail = (value) => {
    return String(value).toLowerCase().match(emailRegex);
  };

  const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

  const resendEmail = (newEmail = null) => {
    if (isSubmitting) {
      return false;
    }

    if (resendCount >= 3) {
      setStatus(
        <span className="text-danger">{locale("Unable to send email")}</span>
      );
      return false;
    } else if (newEmail && resendCount >= 3 && newEmail === email) {
      setError(locale("Unable to send email"));
      setStatus(
        <span className="text-danger">{locale("Unable to send email")}</span>
      );
      return false;
    } else if (isObjectEmpty(newEmail)) {
      setError(locale("Email required!"));
      return false;
    } else if (!isObjectEmpty(changedEmail)) {
      if (!validateEmail(changedEmail)) {
        setError(locale("Invalid email"));
        return false;
      } else {
        newEmail = changedEmail.trim();
      }
    } else if (!isObjectEmpty(newEmail)) {
      newEmail = String(newEmail).trim();
    } else {
      setStatus(<span className="text-danger">{locale("Invalid email")}</span>);
      setError(locale("Invalid email"));
      return false;
    }

    const currentEmail = email || authData?.email || "";
    const isResendingCurrentEmail =
      normalizeEmail(newEmail) === normalizeEmail(currentEmail);

    setIsSubmitting(true);
    changeEmailForUnconfirmed({
      variables: {
        email: newEmail,
        team_id: currentTeamId,
        onResendEmail: isResendingCurrentEmail,
      },
    })
      .then((results) => {
        const response = results.data && results.data.changeEmailForUnconfirmed;
        const wasSuccessful = response && response.status;
        const responseMessage =
          response && response.message
            ? response.message
            : locale("messages:backend.something went wrong.");

        if (wasSuccessful && typeof fetchUserSettings === "function") {
          fetchUserSettings();
        }

        if (
          wasSuccessful &&
          newEmail === email
        ) {
          Session.storeSessionVar("resend", parseInt(resendCount) + 1, 3600);
          setResendCount(parseInt(resendCount) + 1);
          setStatus(
            <span className="email-verification-status email-verification-status--success">
              {locale("Verification email sent successfully to {{newEmail}}", {
                newEmail: newEmail,
              })}
            </span>
          );
          setError("");
        } else if (
          wasSuccessful &&
          newEmail !== email
        ) {
          setStatus(
            <span className="email-verification-status email-verification-status--success">
              {locale("Verification email sent successfully to {{newEmail}}", {
                newEmail: newEmail,
              })}
            </span>
          );
          setError("");
        } else {
          setError(responseMessage);
          setStatus(
            <span className="text-danger">
              {responseMessage}
            </span>
          );
        }
      })
      .catch((error) => {
        console.log(error);
        let { graphQLErrors } = error;
        console.log("Error:", graphQLErrors);
        const myError = checkErrorExists(getValidationMessage(graphQLErrors), locale);
        setError(myError);
        setStatus(<span className="text-danger">{myError}</span>);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="container-fluid px-0 email-verification-premium">
      <div className="row mx-0">
        <div className="col-md-6 px-0">
          <div className="login-signup-form">
            <div className="form-inner-main-bar">
              <div className="brand-logo-form">
                <img src={fspLogo} alt="" />
                <a
                  className="btn btn-lg btn-primary text-nowrap glossy_button"
                  href={scheduleAppointmentUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Schedule Demo
                </a>
              </div>
              <div className="form-field-bar">
                {!isChangeEmail && (
                  <div className="form-field-bar-inner email-verification-card">
                    <h2 className="mt-3 text-dark">Verify Your Email</h2>
                    <p className="email-verification-copy">
                      We&apos;ve sent a verification email to{" "}
                      <b>{changedEmail ? changedEmail : email}</b>. Click the
                      link to activate your account and continue building your
                      business flow.
                    </p>
                    <h5 className="mb-4 email-verification-helper">
                      Didn&apos;t receive it or used the wrong address?
                    </h5>
                    <p className="text-center">{status ? status : " "}</p>
                    <button
                      className="btn btn-lg btn-primary w-100 mb-3 email-verification-primary-btn"
                      type="button"
                      onClick={() => {
                        if (resendCount < 3) {
                          resendEmail(email);
                        }
                      }}
                      disabled={resendCount >= 3 || isSubmitting}
                    >
                      {isSubmitting ? (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : null}
                      {isSubmitting
                        ? "Sending..."
                        : "Send Verification Email Again"}
                    </button>

                    <button
                      className="btn btn-lg btn-outline-primary w-100 mb-4 email-verification-secondary-btn"
                      type="button"
                      onClick={() => {
                        setIsChangeEmail(true);
                        setStatus(null);
                      }}
                      disabled={isSubmitting}
                    >
                      Change Email
                    </button>
                  </div>
                )}

                {isChangeEmail && resendCount < 3 && (
                  <div className="form-field-bar-inner email-verification-card">
                    <h2 className="mt-3 text-dark">
                      Verify Your Account for Continued Use
                    </h2>
                    {["valid", "", null].indexOf(emailStatus) === -1 && (
                      <p className="email-verification-copy">
                        We noticed a <b>[Disposable]</b> email was used during
                        signup. To reactivate your account, please provide your
                        business email for verification below.
                      </p>
                    )}
                    <p>{status ? status : " "}</p>
                    <Form>
                      <Row>
                        <Col sm="12">
                          <Form.Group className="form-group">
                            <Form.Label>Email Address</Form.Label>
                            <Form.Control
                              className={error ? "is-invalid" : ""}
                              id={"email"}
                              type="text"
                              value={changedEmail}
                              onChange={(e) => {
                                setChangedEmail(e.target.value);
                              }}
                              placeholder="Enter Your Email Address"
                              disabled={isSubmitting}
                            />
                            <InLineError
                              type="error"
                              name="email"
                              message={error}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <button
                        className="btn btn-lg btn-primary w-100 my-2 email-verification-primary-btn"
                        type="button"
                        onClick={() => resendEmail(changedEmail)}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        ) : null}
                        {isSubmitting ? "Verifying..." : "Verify"}
                      </button>{" "}
                      <button
                        className="btn btn-lg btn-outline-primary w-100 my-2 email-verification-secondary-btn"
                        type="button"
                        onClick={() => {
                          setIsChangeEmail(false);
                          setStatus(null);
                          setError(null);
                        }}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                    </Form>

                    <div className="or-divider text-uppercase my-3">
                      <span>OR</span>
                    </div>
                    <div className="text-center mb-4 email-verification-helper">
                      If you believe this is a mistake and your email is
                      legitimate, contact us at <b>info@saleoid.com</b>{" "}
                      or schedule a call with us. 
                    </div>
                    <a
                      className="btn btn-lg btn-outline-primary w-100 mb-4 email-verification-outline-btn"
                      href={scheduleAppointmentUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Schedule Call
                    </a>
                  </div>
                )}

                {/*--- Next Page ---*/}
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 px-0">
          <SignupSlider />
        </div>
      </div>
    </div>
  );
};
export default EmailVerification;
